import { isEmpty, set, startCase } from 'lodash';
import type { FieldPath } from 'react-hook-form';
import { coreApi } from '$api-clients/clients';
import type { CoreSchema } from '$api-clients/generated';

type OwnershipInput = {
  name?: string | null;
  title?: string | null;
  titleOther?: string | null;
};

type InteractionContactGeneralInput = {
  cellPhone?: string | null;
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  licenseNumber?: string | null;
  nickname?: string | null;
};

type BrokerageInput = {
  cellPhone?: string | null;
  city?: string | null;
  companyName?: string | null;
  county?: string | null;
  email?: string | null;
  fax?: string | null;
  jurisdiction?: string | null;
  licenseNumber?: string | null;
  mlsCode?: string | null;
  mlsId?: string | null;
  officeEmail?: string | null;
  phone?: string | null;
  state?: string | null;
  street1?: string | null;
  street2?: string | null;
  taxId?: string | null;
  zip?: string | null;
};

export type ContactInput = {
  addressTypeUsed?: string | null;
  avatar?: string | null;
  broker?: InteractionContactGeneralInput | null;
  brokerage?: BrokerageInput | null;
  cellPhone?: string | null;
  city?: string | null;
  commission?: string | null;
  companyName?: string | null;
  contactSource?: string | null;
  country?: string | null;
  county?: string | null;
  email?: string | null;
  entity?: OwnershipInput | null;
  entityType?: string | null;
  etag?: string | null;
  externalId?: string | null;
  fax?: string | null;
  firstName?: string | null;
  googleContactId?: string | null;
  homePhone?: string | null;
  id?: string | null;
  isSelfRepresented?: boolean | null;
  isSuccessor?: boolean | null;
  jurisdiction?: string | null;
  lastName?: string | null;
  licenseNumber?: string | null;
  listingPeriod?: string | null;
  mlsId?: string | null;
  nickname?: string | null;
  officeManager?: InteractionContactGeneralInput | null;
  officeNumber?: string | null;
  order?: number | null;
  powerOfAttorney?: InteractionContactGeneralInput | null;
  primaryMls?: string | null;
  probate?: OwnershipInput | null;
  role?: string | null;
  sellingCommission?: string | null;
  state?: string | null;
  street1?: string | null;
  street2?: string | null;
  supervisingBroker?: InteractionContactGeneralInput | null;
  teamBusinessLicenseEntityNumber?: string | null;
  teamId?: string | null;
  teamName?: string | null;
  trust?: OwnershipInput | null;
  type?: string | null;
  userId?: string | null;
  zip?: string | null;
};

type GoogleContactBody =
  CoreSchema.paths['/user-settings/{id}/google-contact']['post']['requestBody']['content']['application/json'];

const booleanGoogleContactDataKeys = ['isSuccessor'];

// TODO: CORE-6720 - Should be moved to configs
export const googleContactDataKeys = [
  'firstName',
  'isSuccessor',
  'lastName',
  'email',
  'entityType',
  'homePhone',
  'cellPhone',
  'street1',
  'street2',
  'city',
  'state',
  'zip',
  'contactSource',
  'licenseNumber',
  'brokerage.licenseNumber',
  'brokerage.companyName',
  'brokerage.street1',
  'brokerage.street2',
  'brokerage.city',
  'brokerage.zip',
  'brokerage.state',
  'trust.name',
  'trust.title',
  'trust.titleOther',
  'entity.name',
  'entity.title',
  'entity.titleOther',
  'probate.name',
  'probate.title',
  'probate.titleOther',
  'powerOfAttorney.firstName',
  'powerOfAttorney.lastName',
  'powerOfAttorney.email',
  'companyName',
] satisfies FieldPath<ContactInput>[];

export function getContactPayload(contactFields: string[]): ContactInput {
  return contactFields.reduce((agg, value, fieldIndex) => {
    const key = googleContactDataKeys[fieldIndex];
    if (booleanGoogleContactDataKeys.includes(key)) {
      return set(agg, key, value === 'true');
    }
    return set(agg, googleContactDataKeys[fieldIndex], value);
  }, {});
}

function getRegion({ state, county }: Pick<ContactInput, 'state' | 'county'>) {
  if (county && state) {
    return `${county}\n${state}`;
  }
  if (county) {
    return county;
  }
  return state;
}

function buildUserDefinedData(prop: string, value?: string | null) {
  return value ? { key: startCase(prop), value } : {};
}

function buildNamesPayload(contact: ContactInput) {
  const { firstName, lastName } = contact;
  if (!lastName && !firstName) return {};
  return {
    names: [
      {
        ...(lastName ? { familyName: lastName } : {}),
        ...(firstName ? { givenName: firstName } : {}),
        displayName: [firstName, lastName].filter(Boolean).join(' '),
      },
    ],
  };
}

function buildEmailAddressPayload(contact: ContactInput) {
  if (!contact.email) return {};
  return {
    emailAddresses: [{ value: contact.email }],
  };
}

function buildPhoneNumbersPayload(contact: ContactInput) {
  if (!contact.cellPhone && !contact.homePhone) return {};
  return {
    phoneNumbers: [
      ...(contact.cellPhone
        ? [{ type: 'mobile', value: contact.cellPhone }]
        : []),
      ...(contact.homePhone
        ? [{ type: 'home', value: contact.homePhone }]
        : []),
    ],
  };
}

function buildOrganizationsPayload(contact: ContactInput) {
  if (!contact.companyName) return {};
  return {
    organizations: [{ name: contact.companyName }],
  };
}

function buildAddressPayload(contact: ContactInput) {
  const { street1, street2, city, zip, country, addressTypeUsed } = contact;
  const region = getRegion(contact);
  if (!street1 && !street2 && !city && !zip && !country && !region) {
    return {};
  }
  return {
    addresses: [
      {
        ...(street1 ? { streetAddress: street1 } : {}),
        ...(street2 ? { extendedAddress: street2 } : {}),
        ...(city ? { city } : {}),
        ...(region ? { region } : {}),
        ...(country ? { country } : {}),
        ...(zip ? { postalCode: zip } : {}),
        type: addressTypeUsed || 'home',
      },
    ],
  };
}

export function generateGoogleContactPayload(
  contact: ContactInput,
): GoogleContactBody {
  return {
    etag: contact.etag,
    ...buildNamesPayload(contact),
    ...buildAddressPayload(contact),
    ...buildEmailAddressPayload(contact),
    ...buildPhoneNumbersPayload(contact),
    ...buildOrganizationsPayload(contact),
    userDefined: [
      buildUserDefinedData('licenseNumber', contact.licenseNumber),
      buildUserDefinedData('companyName', contact.companyName),
      buildUserDefinedData('entityType', contact.entityType),
      buildUserDefinedData('isSuccessor', contact.isSuccessor?.toString()),
      buildUserDefinedData('role', contact.role),
      buildUserDefinedData(
        'brokerage.companyName',
        contact.brokerage?.companyName,
      ),
      buildUserDefinedData('brokerage.street1', contact.brokerage?.street1),
      buildUserDefinedData('brokerage.street2', contact.brokerage?.street2),
      buildUserDefinedData('brokerage.city', contact.brokerage?.city),
      buildUserDefinedData('brokerage.state', contact.brokerage?.state),
      buildUserDefinedData('brokerage.zip', contact.brokerage?.zip),
      buildUserDefinedData('brokerage.cellPhone', contact.brokerage?.cellPhone),
      buildUserDefinedData('brokerage.email', contact.brokerage?.email),
      buildUserDefinedData(
        'brokerage.officeEmail',
        contact.brokerage?.officeEmail,
      ),
      buildUserDefinedData('brokerage.mlsCode', contact.brokerage?.mlsCode),
      buildUserDefinedData('brokerage.mlsId', contact.brokerage?.mlsId),
      buildUserDefinedData('brokerage.fax', contact.brokerage?.fax),
      buildUserDefinedData('brokerage.phone', contact.brokerage?.phone),
      buildUserDefinedData(
        'brokerage.licenseNumber',
        contact.brokerage?.licenseNumber,
      ),
      buildUserDefinedData('brokerage.county', contact.brokerage?.county),
      buildUserDefinedData('entity.name', contact.entity?.name),
      buildUserDefinedData('entity.title', contact.entity?.title),
      buildUserDefinedData('entity.titleOther', contact.entity?.titleOther),
      buildUserDefinedData('trust.name', contact.trust?.name),
      buildUserDefinedData('trust.title', contact.trust?.title),
      buildUserDefinedData('trust.titleOther', contact.trust?.titleOther),
      buildUserDefinedData('probate.name', contact.probate?.name),
      buildUserDefinedData('probate.title', contact.probate?.title),
      buildUserDefinedData('probate.titleOther', contact.probate?.titleOther),
      buildUserDefinedData(
        'powerOfAttorney.firstName',
        contact.powerOfAttorney?.firstName,
      ),
      buildUserDefinedData(
        'powerOfAttorney.lastName',
        contact.powerOfAttorney?.lastName,
      ),
      buildUserDefinedData(
        'powerOfAttorney.email',
        contact.powerOfAttorney?.email,
      ),
    ].filter((val) => !isEmpty(val)),
  };
}

/**
 * Calls PUT (update) or POST (create) to the Google Contact endpoint
 * based on whether a googleContactId is provided.
 * Returns the google contact ID.
 */
export async function saveOrCreateGoogleContact({
  ownerId,
  googleContactId,
  contact,
}: {
  ownerId: string;
  googleContactId?: string | null;
  contact: ContactInput;
}): Promise<string> {
  const body = generateGoogleContactPayload(contact);

  if (googleContactId) {
    const { error } = await coreApi.PUT(
      '/user-settings/{id}/google-contact/{googleContactId}',
      {
        params: { path: { id: ownerId, googleContactId } },
        body,
      },
    );

    if (error !== undefined) {
      throw new Error('Failed to update Google Contact', { cause: error });
    }

    return googleContactId;
  }

  const { data, error } = await coreApi.POST(
    '/user-settings/{id}/google-contact',
    {
      params: { path: { id: ownerId } },
      body,
    },
  );

  if (error !== undefined || !data.resourceName) {
    throw new Error('Failed to create Google Contact', { cause: error });
  }

  return data.resourceName.replace('people/', '');
}
